@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginekartei.KarteiApi
import org.example.project.service.InfrastrukturApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.utils.All
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div

@Composable
fun CUSWAPlatzTorverwaltung(art: String = org.example.project.service.currentSaveArt()) {

    val scope = rememberCoroutineScope()

    var waPlaetze by remember { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var tore by remember { mutableStateOf<List<All>>(emptyList()) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }

    LaunchedEffect(art) {
        standorte = KarteiApi.loadStandorte().sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }

        waPlaetze = letzteFilter?.let { InfrastrukturApi.searchPlaetze("WAPlatz", it) }.orEmpty()
            .filter { it.typ.trim().equals("WAPlatz", ignoreCase = true) }

        tore = InfrastrukturApi.loadTore()
            .filter { it.gesperrt != true }
    }

    val standortIds = remember(waPlaetze, tore) {
        (waPlaetze.map { it.standort } + tore.map { it.standort })
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()
            .sortedWith(compareBy<String> { it.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it })
    }

    LaunchedEffect(standortIds) {
        if (selectedStandort.isNotBlank() && selectedStandort !in standortIds) {
            selectedStandort = ""
        }
    }

    if (waPlaetze.isEmpty() || tore.isEmpty()) return

    val gefiltertePlaetze = waPlaetze.filter { selectedStandort.isBlank() || it.standort == selectedStandort }
    val torListe = tore
        .filter { selectedStandort.isBlank() || it.standort == selectedStandort }
        .mapNotNull { it.tor }
        .distinct()
        .sorted()

    if (gefiltertePlaetze.isEmpty() || torListe.isEmpty()) return

    val headers = buildList {
        add("ID")
        add("WA-Platz")
        torListe.forEach { add("Tor $it") }
    }

    val data = gefiltertePlaetze.map { p ->
        val row = buildList {
            add(p.id)
            add(p.platzcode)
            torListe.forEach { tor ->
                add(if (p.tore.contains(tor)) "✅" else "⬜")
            }
        }
        p.id to row
    }

    TabularScreen(
        screenKey = "WAPlatzTorverwaltung",
        rollenKey = "WAPlatzTorverwaltung",
        title = "WA-Platz und erlaubte Tore",
        headers = headers,
        data = data,
        editable = true,
        nonEditableCols = setOf("ID", "WA-Platz"),
        toggleColumns = headers.drop(2).toSet(),
        extraButtons = {
            Div({ style { width(240.px) } }) {
                AppDropdown(
                    options = buildList {
                        add(All(key = "", label = "Alle Standorte"))
                        addAll(standortIds.map { id ->
                            val standort = standorte.firstOrNull { it.id == id }
                            All(
                                key = id,
                                label = standort?.standort?.takeIf { it.isNotBlank() } ?: "Standort $id"
                            )
                        })
                    },
                    selectedValue = selectedStandort,
                    onChange = { selectedStandort = it },
                    appearance = AppDropdownAppearance.Field,
                    menuMode = AppDropdownMenuMode.Overlay
                )
            }
        },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                waPlaetze = InfrastrukturApi.searchPlaetze("WAPlatz", filters)
            }
        },
        onCommit = { rows ->
            scope.launch {
                rows.forEach { (id, cols) ->
                    val originalPlatz = waPlaetze.firstOrNull { it.id == id } ?: return@forEach

                    val neueTore = mutableListOf<Int>()
                    torListe.forEachIndexed { index, torNummer ->
                        if (cols[index + 2] == "✅") {
                            neueTore.add(torNummer)
                        }
                    }

                    val updated = originalPlatz.copy(tore = neueTore.sorted())

                    val changed = originalPlatz != updated
                    if (changed) {
                        InfrastrukturApi.savePlatz(updated)
                    }
                }
            }
        }
    )
}
